import * as React from 'react';
import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Paper, TextField, LinearProgress } from '@mui/material';
import { sendServerRequest } from '../../../server/request';
import { openGlobalSnack } from '../../utils/snack';
import { CircularLoading } from '../../utils/loading';

const ENDPOINT = '/server/cltCommand/consultLimit';

type Bucket = 'v8' | 'suma';
type ProviderDefaults = Record<Bucket, number>;
type LoginUsage = { provider: Bucket; login: string; count: number; max: number };

const BUCKETS: Bucket[] = ['v8', 'suma'];
const LABELS: Record<Bucket, string> = { v8: 'V8', suma: 'Suma' };

function usageColor(count: number, max: number): 'success' | 'warning' | 'error' {
  if (max <= 0 || count >= max) return 'error';
  if (count >= max * 0.9) return 'warning';
  return 'success';
}

export function ConsultLimitPanel() {
  const [defaults, setDefaults] = useState<ProviderDefaults>({ v8: 0, suma: 0 });
  const [usage, setUsage] = useState<LoginUsage[]>([]);
  const [serverSnapshot, setServerSnapshot] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  function applyResponse(data: any) {
    const d: ProviderDefaults = data?.config?.providerDefaults ?? { v8: 0, suma: 0 };
    const u: LoginUsage[] = data?.usage ?? [];
    setDefaults(d);
    setUsage(u);
    setServerSnapshot(JSON.stringify(d));
  }

  function load() {
    setLoading(true);
    sendServerRequest('GET', `${ENDPOINT}/get`)
      .then((e) => {
        applyResponse(e.data?.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar os limites de consulta.', 'error');
      });
  }

  function setDefault(bucket: Bucket, value: string) {
    const n = parseInt(value, 10);
    setDefaults((prev) => ({ ...prev, [bucket]: Number.isFinite(n) && n >= 0 ? n : 0 }));
  }

  function save() {
    setSaving(true);
    sendServerRequest('POST', `${ENDPOINT}/set`, { providerDefaults: defaults })
      .then((e) => {
        applyResponse(e.data?.data);
        setSaving(false);
        openGlobalSnack('Limites de consulta atualizados.', 'success');
      })
      .catch(() => {
        setSaving(false);
        openGlobalSnack('Não foi possível salvar os limites.', 'error');
      });
  }

  const dirty = JSON.stringify(defaults) !== serverSnapshot;

  if (loading) return <CircularLoading />;

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
      <h3 style={{ marginBottom: 4 }}>Limite de consultas por login (24h)</h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0, marginBottom: 16 }}>
        Teto de consultas de cada login numa janela deslizante de 24h. Ao atingir o
        teto, o provedor fica bloqueado e a consulta tenta outro; se todos estiverem
        no teto, fica em espera até liberar.
      </p>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
        {BUCKETS.map((b) => (
          <TextField
            key={b}
            label={`Limite ${LABELS[b]}`}
            type="number"
            size="small"
            value={defaults[b]}
            onChange={(e) => setDefault(b, e.target.value)}
            inputProps={{ min: 0 }}
            style={{ maxWidth: 160 }}
          />
        ))}
      </div>

      <h4 style={{ marginBottom: 8 }}>Consultas na janela (24h)</h4>
      {usage.length === 0 && (
        <p style={{ fontSize: 12, color: '#000000a0' }}>Sem consultas nas últimas 24h.</p>
      )}
      {usage.map((u) => {
        const pct = u.max > 0 ? Math.min(100, (u.count / u.max) * 100) : 100;
        const color = usageColor(u.count, u.max);
        return (
          <Paper
            key={`${u.provider}:${u.login}`}
            variant="outlined"
            style={{ padding: '8px 12px', marginBottom: 8, textAlign: 'left' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span>
                <strong>{LABELS[u.provider]}</strong>
                <span style={{ fontSize: 12, color: '#000000a0', marginLeft: 8 }}>{u.login}</span>
              </span>
              <span style={{ fontWeight: 600 }}>
                {u.count} / {u.max}
              </span>
            </div>
            <LinearProgress variant="determinate" value={pct} color={color} />
          </Paper>
        );
      })}

      <div style={{ marginTop: 16 }}>
        <LoadingButton loading={saving} disabled={!dirty} variant="contained" onClick={save}>
          Salvar limites
        </LoadingButton>
      </div>
    </div>
  );
}
